import type { AsyncStatus } from "@react-hookz/web";
import type { Dispatch, SetStateAction } from "react";
import { useEffect } from "react";
import { useStore } from "zustand";

import { useGeneratedInsightsLoader } from "../../../../hooks/generatedInsights/useGeneratedInsightsLoader";
import { navigationStore } from "../../../../store/navigation";
import { isSuccess, parseSharableLinkId } from "../../../../utils/api.utils";
import { withInsightsRenderer } from "../withInsightsRenderer";

import { GeneratedInsightCard } from "./GeneratedInsightCard";

interface GeneratedInsightsProps {
    isLoaderInView: boolean;
    setAsyncStatus: Dispatch<SetStateAction<AsyncStatus>>;
    setTotalCount: Dispatch<SetStateAction<number>>;
}

export const GeneratedInsights: React.FunctionComponent<
    GeneratedInsightsProps
> = (props: GeneratedInsightsProps) => {
    const { isLoaderInView, setAsyncStatus, setTotalCount } = props;
    const [asyncStatus, lazyLoadedInsights, totalResults, allInsights] =
        useGeneratedInsightsLoader({
            isLoaderInView,
        });

    useEffect(() => {
        setAsyncStatus(asyncStatus);
    }, [asyncStatus]);

    const rawIdentifier = useStore(
        navigationStore,
        (state) => state.identifier,
    );
    const insightIdentifier = rawIdentifier
        ? parseSharableLinkId(rawIdentifier).identifier
        : undefined;
    const insights = insightIdentifier
        ? allInsights.filter(
              (insight) => insight.identifier === insightIdentifier,
          )
        : lazyLoadedInsights;

    useEffect(() => {
        if (isSuccess(asyncStatus)) {
            setTotalCount(insightIdentifier ? insights.length : totalResults);
        }
    }, [asyncStatus, totalResults, insightIdentifier, insights.length]);

    return (
        <>
            {insights.map((insight, index) => (
                <GeneratedInsightCard
                    key={`generated-${index}-${insight.identifier}`}
                    insight={insight}
                />
            ))}
        </>
    );
};

export const GeneratedInsightsViewer = withInsightsRenderer(GeneratedInsights);
